import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableFooter,
  TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, AlertCircle, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { dashboardApi, AiUsageStatus } from '@/api/dashboardApi';

// ── 타입 ────────────────────────────────────────────────────
interface DailyRow {
  date: string;
  total: number;
  uiTotal: number;
  WEBSITE_CRAWLING: number;
  IMAGE_URL: number;
  IMAGE_UPLOAD: number;
  ui_IMAGE_URL?: number;
  ui_IMAGE_UPLOAD?: number;
  openApiTotal: number;
  openApi_IMAGE_URL: number;
  openApi_IMAGE_UPLOAD: number;
}

interface GrandTotal extends Omit<DailyRow, 'date'> {}

interface DailySummaryData {
  targetUserId: string | null;
  startDate: string;
  endDate: string;
  rows: DailyRow[];
  grandTotal: GrandTotal;
}

// ── 헬퍼 ────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
const currentMonthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

export default function AiUsageDailyPage() {
  const { user, token } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  const [searchParams] = useSearchParams();

  const [startDate, setStartDate] = useState(() => {
    const paramStart = searchParams.get('startDate');
    if (paramStart) return paramStart;
    return currentMonthStart(); // 기본값 (청구 기간 로드 후 업데이트됨)
  });
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || today());
  const [filterUserId, setFilterUserId] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DailySummaryData | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<{ startDate: string; endDate: string; nextBillingDate: string } | null>(null);

  // OPEN_API 컬럼 접기/펼치기
  const [showOpenApi, setShowOpenApi] = useState(true);

  // 개인/팀 조회 체크박스 (팀장만 표시)
  const [isTeamView, setIsTeamView] = useState(false);

  // 항상 최신 필터 값을 유지하는 ref (fetchData가 stale closure 없이 최신 값을 읽을 수 있게)
  const filtersRef = useRef({ token, startDate, endDate, filterUserId, isSuperAdmin, isTeamView });
  filtersRef.current = { token, startDate, endDate, filterUserId, isSuperAdmin, isTeamView };

  // AI 사용량 상태 (팀 컨텍스트 포함)
  const { data: aiUsageStatus } = useQuery({
    queryKey: ['aiUsageStatus'],
    queryFn: () => dashboardApi.getAiUsageStatus(),
  });

  // 청구 기간 로드
  useEffect(() => {
    const loadBillingPeriod = async () => {
      try {
        const period = await dashboardApi.getBillingPeriod();
        setBillingPeriod(period);
        // searchParams에 startDate가 없을 때만 청구기간 시작일로 설정
        const paramStart = searchParams.get('startDate');
        if (!paramStart && period) {
          setStartDate(period.startDate);
        }
      } catch {
        // 청구 기간 로드 실패 시 무시 (기본값 사용)
      }
    };
    loadBillingPeriod();
  }, []); // searchParams는 처음에만 확인하면 됨

  // startDate가 변경되면 fetchData 자동 호출
  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = useCallback(async () => {
    const { token: tk, startDate: sd, endDate: ed, filterUserId: uid, isSuperAdmin: isAdmin, isTeamView: teamView } = filtersRef.current;
    if (!tk) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (sd) params.append('startDate', sd);
    if (ed) params.append('endDate', ed);
    if (isAdmin && uid.trim()) params.append('userId', uid.trim());
    if (teamView) params.append('teamView', 'true');

    try {
      const res = await fetch(`${BASE}/api/dashboard/ai-usage/daily-summary?${params}`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json.data ?? json);
    } catch (e: any) {
      setError(e.message || '데이터 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []); // filtersRef를 통해 최신 값 읽으므로 deps 불필요

  // 첫 마운트 시 fetchData 호출 (초기 로드)
  useEffect(() => {
    fetchData();
  }, []); // 빈 dependency로 마운트 시 1회만 실행

  const numCell = (v: number) => (
    <TableCell className="text-right">{v > 0 ? v.toLocaleString() : <span className="text-gray-400">0</span>}</TableCell>
  );

  const gt = data?.grandTotal;

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || 'AI 이용 현황'}
        description="날짜별 서비스 이용 건수 집계 (UI + OPEN API)"
        icon={menuIcon}
      />

      {/* 조회 조건 */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label>시작일</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label>종료일</Label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            {isSuperAdmin && (
              <div className="space-y-1">
                <Label>사용자 ID (SUPER_ADMIN)</Label>
                <Input
                  type="text"
                  placeholder="전체 (비워두면 모든 사용자)"
                  value={filterUserId}
                  onChange={e => setFilterUserId(e.target.value)}
                  className="w-56"
                />
              </div>
            )}
            {aiUsageStatus?.isTeamContext && aiUsageStatus?.role === 'OWNER' && (
              <div className="space-y-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="teamView"
                  checked={isTeamView}
                  onChange={e => setIsTeamView(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="teamView" className="cursor-pointer">팀 전체 조회</Label>
              </div>
            )}
            <Button onClick={fetchData} disabled={loading} className="h-10">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              조회
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 합계 카드 */}
      {gt && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-blue-600 font-medium mb-1">전체 합계</p>
              <p className="text-2xl font-bold text-blue-800">{gt.total.toLocaleString()}<span className="text-sm font-normal ml-1">건</span></p>
              {aiUsageStatus?.isTeamContext && (
                <p className="text-xs text-blue-500 mt-1">
                  {aiUsageStatus.role === 'OWNER' ? '팀 전체' : '본인'} 사용량
                  {aiUsageStatus.role === 'OWNER' && aiUsageStatus.teamTotalUsed !== null && (
                    <span> (팀: {aiUsageStatus.teamTotalUsed.toLocaleString()}건)</span>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-indigo-600 font-medium mb-1">UI 방식 합계</p>
              <p className="text-2xl font-bold text-indigo-800">{gt.uiTotal.toLocaleString()}<span className="text-sm font-normal ml-1">건</span></p>
              <p className="text-xs text-indigo-500 mt-1">크롤링 {gt.WEBSITE_CRAWLING} · URL {gt.IMAGE_URL ?? gt.ui_IMAGE_URL ?? 0} · 업로드 {gt.IMAGE_UPLOAD ?? gt.ui_IMAGE_UPLOAD ?? 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-purple-600 font-medium mb-1">OPEN API 합계</p>
              <p className="text-2xl font-bold text-purple-800">{gt.openApiTotal.toLocaleString()}<span className="text-sm font-normal ml-1">건</span></p>
              <p className="text-xs text-purple-500 mt-1">URL {gt.openApi_IMAGE_URL} · 업로드 {gt.openApi_IMAGE_UPLOAD}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-gray-600 font-medium mb-1">조회 기간</p>
              <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">{data?.startDate} ~ {data?.endDate}</p>
            </CardContent>
          </Card>
          {/* 엔터프라이즈 계약 정보 */}
          {aiUsageStatus?.enterpriseContract && (
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-purple-600 font-medium mb-1">엔터프라이즈 계약</p>
                <p className="text-sm font-semibold text-purple-800">{aiUsageStatus.enterpriseContract.contractName}</p>
                <p className="text-xs text-purple-500 mt-1">월 한도: {aiUsageStatus.enterpriseContract.monthlyLimit.toLocaleString()}건</p>
                <p className="text-xs text-purple-500">월 이용료: ₩{aiUsageStatus.enterpriseContract.monthlyPrice.toLocaleString()}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* 상세 테이블 */}
      {data && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">일별 상세</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOpenApi(v => !v)}
                className="text-xs"
              >
                {showOpenApi ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                OPEN API {showOpenApi ? '접기' : '펼치기'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>날짜</TableHead>
                  <TableHead className="text-right">전체</TableHead>
                  <TableHead className="text-right text-indigo-600">UI 합계</TableHead>
                  <TableHead className="text-right text-indigo-500">크롤링</TableHead>
                  <TableHead className="text-right text-indigo-500">이미지 URL</TableHead>
                  <TableHead className="text-right text-indigo-500">이미지 업로드</TableHead>
                  {showOpenApi && (
                    <React.Fragment key="openapi-header">
                      <TableHead className="text-right text-purple-600">API 합계</TableHead>
                      <TableHead className="text-right text-purple-500">API URL</TableHead>
                      <TableHead className="text-right text-purple-500">API 업로드</TableHead>
                    </React.Fragment>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={showOpenApi ? 9 : 6}
                      className="text-center text-gray-400 py-10"
                    >
                      조회된 데이터가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.rows.map((row) => (
                    <TableRow key={row.date}>
                      <TableCell className="font-mono">{row.date}</TableCell>
                      <TableCell className="text-right font-semibold">{row.total > 0 ? row.total.toLocaleString() : <span className="text-gray-400">0</span>}</TableCell>
                      {numCell(row.uiTotal)}
                      {numCell(row.WEBSITE_CRAWLING)}
                      {numCell(row.IMAGE_URL ?? row.ui_IMAGE_URL ?? 0)}
                      {numCell(row.IMAGE_UPLOAD ?? row.ui_IMAGE_UPLOAD ?? 0)}
                      {showOpenApi && (
                        <React.Fragment key="openapi-row">
                          {numCell(row.openApiTotal)}
                          {numCell(row.openApi_IMAGE_URL)}
                          {numCell(row.openApi_IMAGE_UPLOAD)}
                        </React.Fragment>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
              {gt && (
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-semibold">합계</TableCell>
                    <TableCell className="text-right font-semibold">{gt.total.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-semibold text-indigo-600">{gt.uiTotal.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-indigo-500">{gt.WEBSITE_CRAWLING.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-indigo-500">{(gt.IMAGE_URL ?? gt.ui_IMAGE_URL ?? 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right text-indigo-500">{(gt.IMAGE_UPLOAD ?? gt.ui_IMAGE_UPLOAD ?? 0).toLocaleString()}</TableCell>
                    {showOpenApi && (
                      <React.Fragment key="openapi-footer">
                        <TableCell className="text-right font-semibold text-purple-600">{gt.openApiTotal.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-purple-500">{gt.openApi_IMAGE_URL.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-purple-500">{gt.openApi_IMAGE_UPLOAD.toLocaleString()}</TableCell>
                      </React.Fragment>
                    )}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}
